import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listPackOrdersGrouped } from "@/lib/pack.functions";
import { packIdenticalBatch } from "@/lib/fulfillment.functions";
import { listServicesForPack } from "@/lib/carrier-catalog.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Printer, ArrowLeft, Package, ScanLine, Check, MapPin, Layers, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { RateShopPanel } from "@/components/pack/rate-shop-panel";
import { RoutingExplanationPanel } from "@/components/pack/routing-explanation-panel";

export const Route = createFileRoute("/_authenticated/app/pack/batch")({
  validateSearch: (input: Record<string, unknown>) => ({
    ids: typeof input.ids === "string" ? input.ids : "",
  }),
  component: BatchPack,
});

/** Carton presets — kept in lock-step with single-order pack station. */
const CARTON_SIZES = [
  { code: "small",  label: "Small  · 9 × 6 × 4 in" },
  { code: "medium", label: "Medium · 12 × 9 × 6 in" },
  { code: "large",  label: "Large  · 16 × 12 × 8 in" },
  { code: "xlarge", label: "X-Large · 20 × 16 × 12 in" },
  { code: "mailer", label: "Poly mailer" },
  { code: "custom", label: "Custom / other" },
] as const;

/** Parcel carriers surfaced in the batch dropdown. Service catalogs are
 *  loaded per-carrier from the server (see `listServicesForPack`) so the
 *  dropdown mirrors what Rate Shop can actually return. */
const CARRIERS = ["UPS", "USPS", "GlobalPost", "FedEx", "DHL"];
const OZ_TO_G = 28.3495;

function normalizeCarrier(c: string | null | undefined): string {
  if (!c) return "";
  const m = CARRIERS.find((x) => x.toLowerCase() === c.toLowerCase());
  return m ?? c;
}

function BatchPack() {
  const { ids } = Route.useSearch();
  const orderIds = ids.split(",").map((s: string) => s.trim()).filter(Boolean);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { tenantId, facilityId, customerId } = useScope();
  const groupedFn = useServerFn(listPackOrdersGrouped);
  const batchFn = useServerFn(packIdenticalBatch);
  const listServicesFn = useServerFn(listServicesForPack);

  // Pack-station banner (set on /app/pack and persisted per facility).
  const [station, setStation] = useState<string | null>(null);
  useEffect(() => {
    if (!facilityId) return;
    const k = `pack-station:${facilityId}`;
    setStation(typeof window !== "undefined" ? window.localStorage.getItem(k) : null);
  }, [facilityId]);

  const { data: grouped, isLoading: groupedLoading } = useQuery({
    queryKey: ["pack-orders-grouped", tenantId, facilityId, customerId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => groupedFn({ data: { tenantId: tenantId!, facilityId: facilityId!, customerId: customerId ?? null } }),
  });

  const group = grouped?.identical.find((g) =>
    g.orders.length === orderIds.length && g.orders.every((o) => orderIds.includes(o.id)),
  );
  const orders = group?.orders ?? [];
  const sharedSkus = group?.shared_skus ?? [];
  const carrierDefault = orders[0]?.carrier ?? "";
  const serviceDefault = orders[0]?.service_level ?? "";

  const [size, setSize] = useState<string>("medium");
  const [weightOz, setWeightOz] = useState<string>("");
  const [carrier, setCarrier] = useState<string>("");
  const [service, setService] = useState<string>("");
  const [tracking, setTracking] = useState<string>(""); // advisory; server doesn't accept per-shipment tracking in batch
  const [chosenRate, setChosenRate] = useState<{ carrier: string; service: string; amount: number } | null>(null);
  const [scan, setScan] = useState<string>("");
  const [scanCounts, setScanCounts] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const scanRef = useRef<HTMLInputElement>(null);

  // Per-carrier service catalog (customer × carrier active flags).
  const orderCustomerId = (orders[0] as any)?.customer_id ?? customerId ?? null;
  const { data: catalogServices = [] } = useQuery({
    queryKey: ["pack-carrier-catalog", tenantId, orderCustomerId, carrier],
    enabled: !!tenantId && !!carrier,
    queryFn: () =>
      listServicesFn({
        data: { tenantId: tenantId!, customerId: orderCustomerId, carrierCode: carrier },
      }),
  });
  const activeServices = catalogServices.filter((s) => s.active);
  const serviceDisplayFor = (code: string): string =>
    catalogServices.find((s) => s.service_code === code)?.display_name ?? code;

  useEffect(() => { scanRef.current?.focus(); }, []);
  useEffect(() => {
    setCarrier((c) => c || normalizeCarrier(carrierDefault));
    setService((s) => s || serviceDefault || "");
  }, [carrierDefault, serviceDefault]);

  // Per-SKU rollup: ordered × N orders, with scanner-verified count.
  const rollup = useMemo(() => sharedSkus.map((s) => ({
    sku_id: s.sku_id,
    sku_code: s.sku_code,
    name: s.name,
    per_order: s.qty,
    target: s.qty * orders.length,
    scanned: scanCounts[s.sku_code] ?? 0,
  })), [sharedSkus, orders.length, scanCounts]);
  const totalTarget = rollup.reduce((a, r) => a + r.target, 0);
  const totalScanned = rollup.reduce((a, r) => a + Math.min(r.scanned, r.target), 0);
  const remaining = Math.max(0, totalTarget - totalScanned);

  const handleScan = (e: React.FormEvent) => {
    e.preventDefault();
    const code = scan.trim();
    if (!code) return;
    const match = sharedSkus.find((s) => s.sku_code === code || s.sku_code.toLowerCase() === code.toLowerCase());
    if (!match) { setScan(""); return toast.error("Unknown SKU — not in this batch"); }
    setScanCounts((p) => ({ ...p, [match.sku_code]: (p[match.sku_code] ?? 0) + 1 }));
    setScan(""); scanRef.current?.focus();
  };

  const submit = async (mode: "label" | "ship_only") => {
    if (orderIds.length === 0) return toast.error("No orders in batch");
    if (!carrier) return toast.error("Select a carrier");
    if (mode === "label" && !service) return toast.error("Select a service");
    if (mode === "ship_only" && !confirm("Ship without purchasing labels? Use only when the customer provides labels.")) return;
    if (remaining > 0 && !confirm(`${remaining} unit(s) not yet scan-verified across the batch. Pack anyway?`)) return;
    setBusy(true);
    try {
      const w = weightOz.trim() === "" ? null : Math.round(Number(weightOz) * OZ_TO_G);
      const res = await runServerFn(
        () => batchFn({ data: {
          orderIds,
          carrier,
          serviceLevel: service,
          packageType: size,
          weightGrams: w,
          printLabels: mode === "label",
        } }),
        mode === "label" ? "Batch pack & print labels" : "Batch pack (no labels)",
      );
      if (res.errors.length > 0) {
        toast.error(`${res.shipmentIds.length} packed, ${res.errors.length} failed`);
      } else {
        toast.success(
          mode === "label"
            ? `Packed ${res.shipmentIds.length} orders · queued ${res.labels?.count ?? 0} labels`
            : `Packed ${res.shipmentIds.length} orders (no labels)`,
        );
      }
      qc.invalidateQueries({ queryKey: ["pack-orders"] });
      qc.invalidateQueries({ queryKey: ["pack-orders-grouped"] });
      qc.invalidateQueries({ queryKey: ["bulk-pack-shipments"] });
      // Route to print queue when labels were requested so the packer sees them flowing.
      navigate({ to: mode === "label" ? "/app/pack/bulk" : "/app/pack" });
    } catch {
      /* toast handled */
    } finally {
      setBusy(false);
    }
  };

  // Batch dissolved / invalid (orders shipped, scope changed, etc.)
  const batchInvalid = !groupedLoading && grouped != null && !group;

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <Link to="/app/pack" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <span className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-muted-foreground" />
              Batch Pack · {orderIds.length} identical
            </span>
            {group && <StatusPill tone={statusTone("picked")} dot>picked</StatusPill>}
          </span>
        }
        description={
          group
            ? `${group.customer_name} · ${carrier || "no carrier"}${service ? ` · ${service}` : ""} · ${remaining} unit(s) unverified`
            : "Loading batch…"
        }
      />
      <div className="p-4 sm:p-6 space-y-4">
        {/* Station banner */}
        {station && (
          <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-sm">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">You're at</span>
            <span className="font-semibold">{station}</span>
            <span className="text-muted-foreground">— labels print to this station's printer.</span>
          </div>
        )}

        {batchInvalid && (
          <div className="rounded-md border-2 border-destructive/40 bg-destructive/10 p-4 text-sm space-y-2">
            <div className="font-semibold text-destructive flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> Batch no longer valid
            </div>
            <p className="text-muted-foreground">
              One or more orders are no longer eligible (status changed, already shipped, or scope filter excludes them). Return to the Pack list and re-select.
            </p>
            <Link to="/app/pack"><Button size="sm" variant="outline"><ArrowLeft className="h-4 w-4 mr-1" />Back to Pack</Button></Link>
          </div>
        )}

        {/* Scan card — mirrors single-order */}
        <Card>
          <CardContent className="space-y-3 pt-6">
            <form onSubmit={handleScan} className="flex gap-2">
              <div className="relative flex-1">
                <ScanLine className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  ref={scanRef}
                  value={scan}
                  onChange={(e) => setScan(e.target.value)}
                  placeholder="Scan SKU to verify (sanity check across the batch)…"
                  className="pl-9 font-mono"
                  autoFocus
                />
              </div>
              <Button type="submit" variant="outline">Verify</Button>
            </form>
            <p className="text-[11px] text-muted-foreground">
              Identical orders are packed by allocation server-side. Scanning here is an optional sanity check — counts increment per SKU until target is reached.
            </p>
          </CardContent>
        </Card>

        {/* Items to Pack — same shape as single-order */}
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Items to Pack ({orderIds.length} orders × shared SKUs)</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {rollup.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">No shared SKUs resolved.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">SKU</TableHead>
                    <TableHead className="text-right text-xs">Per order</TableHead>
                    <TableHead className="text-right text-xs">Target ({orderIds.length}×)</TableHead>
                    <TableHead className="text-right text-xs">Scanned</TableHead>
                    <TableHead className="text-right text-xs">Remaining</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rollup.map((r) => {
                    const rem = Math.max(0, r.target - r.scanned);
                    return (
                      <TableRow key={r.sku_id}>
                        <TableCell className="text-xs font-mono">{r.sku_code}{r.name ? <span className="text-muted-foreground"> — {r.name}</span> : null}</TableCell>
                        <TableCell className="text-right tabular-nums text-xs">{r.per_order}</TableCell>
                        <TableCell className="text-right tabular-nums text-xs">{r.target}</TableCell>
                        <TableCell className="text-right tabular-nums text-xs">{r.scanned}</TableCell>
                        <TableCell className={`text-right tabular-nums text-xs ${rem === 0 ? "text-success" : "text-warning"}`}>{rem}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Orders in batch */}
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Orders in this batch</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-wrap gap-1.5">
              {orders.length === 0 ? (
                <span className="text-xs text-muted-foreground">Resolving…</span>
              ) : orders.map((o) => (
                <Link key={o.id} to="/app/orders/$orderId" params={{ orderId: o.id }}>
                  <Badge variant="secondary" className="font-mono text-[11px] hover:bg-secondary/70 cursor-pointer">{o.order_number}</Badge>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Carton template — single card, parcel-only (matches server contract) */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between py-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Package className="h-4 w-4" />Carton template
              <Badge variant="outline" className="text-[10px]">applied to each of {orderIds.length}</Badge>
            </CardTitle>
            <Input
              type="number" step="0.1" placeholder="Weight per box (oz)" className="w-44 h-8 text-xs"
              value={weightOz} onChange={(e) => setWeightOz(e.target.value)}
            />
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-wrap items-center gap-2">
              <Label className="text-xs text-muted-foreground">Carton size</Label>
              <Select value={size} onValueChange={setSize}>
                <SelectTrigger className="h-8 w-56 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CARTON_SIZES.map((s) => (
                    <SelectItem key={s.code} value={s.code} className="text-xs">{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="ml-auto text-[11px] text-muted-foreground">
                Multi-carton & freight modes: pack individually instead.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Ship card — mirrors single-order minus freight (parcel-only in batch) */}
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Ship</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Carrier</Label>
                <Select value={carrier} onValueChange={(v) => { setCarrier(v); setService(""); }}>
                  <SelectTrigger><SelectValue placeholder="Select carrier" /></SelectTrigger>
                  <SelectContent>
                    {CARRIERS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Service type</Label>
                <Select value={service} onValueChange={setService} disabled={!carrier || activeServices.length === 0}>
                  <SelectTrigger>
                    <SelectValue placeholder={carrier ? (activeServices.length === 0 ? "No services enabled" : "Select service") : "Pick a carrier first"}>
                      {service ? serviceDisplayFor(service) : undefined}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {activeServices.map((s) => (
                      <SelectItem key={s.service_code} value={s.service_code}>{s.display_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {orders[0] && (
              <>
                <RoutingExplanationPanel orderId={orders[0].id} />
                <RateShopPanel
                  orderId={orders[0].id}
                  weightGrams={weightOz.trim() === "" ? 0 : Math.round(Number(weightOz) * OZ_TO_G)}
                  onChoose={(r) => {
                    setChosenRate({ carrier: r.carrier, service: r.service, amount: r.amount });
                    const cNorm = normalizeCarrier(r.carrier);
                    if (cNorm) setCarrier(cNorm);
                    if (r.service_code) setService(r.service_code);
                  }}
                />
                <p className="text-[11px] text-muted-foreground">
                  Rate shown for the first order in the batch — identical orders share weight & destination class, so this rate applies to all {orderIds.length}.
                </p>
              </>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Tracking note (optional)</Label>
                <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Pre-printed master tracking reference" />
              </div>
              <div className="space-y-1">
                <Label>Selected rate</Label>
                <Input
                  readOnly
                  value={chosenRate ? `${chosenRate.carrier} · ${chosenRate.service} · $${chosenRate.amount.toFixed(2)}` : ""}
                  placeholder="Rate-shop above to choose"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer actions — mirrors single-order */}
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="touch" onClick={() => navigate({ to: "/app/pack" })}>Cancel</Button>
            <Button
              variant="outline"
              size="touch"
              onClick={() => submit("ship_only")}
              disabled={busy || orderIds.length === 0 || batchInvalid}
              title="Mark shipped without purchasing labels (customer-provided)"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              Ship batch
            </Button>
            <Button
              size="touch"
              onClick={() => submit("label")}
              disabled={busy || orderIds.length === 0 || batchInvalid}
            >
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Printer className="h-4 w-4 mr-2" />}
              Pack &amp; print {orderIds.length} labels
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}